/**
 * Full-screen product image lightbox with zoom and pan (FND-803).
 *
 * Opened by the gallery (see media-gallery.js). Handles mouse, touch and pen
 * through Pointer Events, so pinch-zoom on mobile and click/wheel zoom on
 * desktop run through the same code path.
 */
if (!customElements.get('product-lightbox')) {
  // Hard ceiling. The real per-image limit is native resolution — see
  // updateMaxScale() — so a low-res master is never blown up and left soft.
  const MAX_SCALE = 4;
  const MIN_SCALE = 1;
  const DOUBLE_TAP_MS = 300;
  const TAP_SLOP_PX = 10;

  class ProductLightbox extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.stage = this.querySelector('.product-lightbox__stage');
      this.slides = Array.from(this.querySelectorAll('.product-lightbox__slide'));
      this.prevBtn = this.querySelector('.product-lightbox__prev');
      this.nextBtn = this.querySelector('.product-lightbox__next');
      this.counterCurrent = this.querySelector('.product-lightbox__counter-current');

      this.index = -1;
      this.scale = 1;
      this.maxScale = MAX_SCALE;
      this.tx = 0;
      this.ty = 0;
      this.pointers = new Map();
      this.lastTap = 0;

      this.addEventListener('click', this.handleClick.bind(this));
      this.stage.addEventListener('pointerdown', this.handlePointerDown.bind(this));
      this.stage.addEventListener('pointermove', this.handlePointerMove.bind(this));
      this.stage.addEventListener('pointerup', this.handlePointerUp.bind(this));
      this.stage.addEventListener('pointercancel', this.handlePointerUp.bind(this));
      this.stage.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });

      this.keydownHandler = this.handleKeydown.bind(this);
      this.resizeHandler = this.handleResize.bind(this);
    }

    /**
     * Opens the lightbox.
     * @param {number} index - Index of the slide to show.
     * @param {Element} [opener] - Element that triggered the open, focused on close.
     */
    open(index = 0, opener = null) {
      if (!this.slides.length) return;

      this.openedBy = opener;
      this.scrollY = window.scrollY;
      document.body.classList.add('fixed');
      document.body.style.top = `-${this.scrollY}px`;

      this.setAttribute('open', '');
      // Force goTo to do its work even when reopening on the same slide.
      this.index = -1;
      this.goTo(index, false);

      document.addEventListener('keydown', this.keydownHandler);
      window.addEventListener('resize', this.resizeHandler);

      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      if (typeof trapFocus === 'function') trapFocus(this, this.querySelector('.product-lightbox__close'));
    }

    /**
     * Closes the lightbox and restores the page behind it.
     */
    close() {
      if (!this.hasAttribute('open')) return;

      document.body.style.top = '';
      document.body.classList.remove('fixed');
      window.scrollTo(0, this.scrollY);

      this.removeAttribute('open');
      this.pointers.clear();

      document.removeEventListener('keydown', this.keydownHandler);
      window.removeEventListener('resize', this.resizeHandler);

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.openedBy);
      } else if (this.openedBy) {
        this.openedBy.focus();
      }
    }

    /**
     * Shows a given slide, resetting any zoom.
     * @param {number} index - Slide index.
     * @param {boolean} [animate=true] - Whether to animate the zoom reset.
     */
    goTo(index, animate = true) {
      const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
      if (clamped === this.index) return;

      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === clamped));
      this.index = clamped;

      // Load the current slide plus its neighbours, so paging feels instant.
      [clamped - 1, clamped, clamped + 1].forEach((i) => this.loadSlide(i));

      this.updateMaxScale();
      this.resetZoom(animate);
      this.updateControls();

      // A slide opened before its image has decoded measures as a zero-width
      // box, which would leave zoom and panning unclamped. Re-measure on load.
      const img = this.activeImage;
      if (img && !img.complete) {
        img.addEventListener('load', () => {
          if (this.activeImage !== img) return;
          this.updateMaxScale();
          this.resetZoom(false);
        }, { once: true });
      }
    }

    /**
     * Swaps a slide's placeholder for its real source.
     * @param {number} index - Slide index.
     */
    loadSlide(index) {
      const slide = this.slides[index];
      if (!slide) return;

      const img = slide.querySelector('.product-lightbox__img');
      if (!img || !img.dataset.src) return;

      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        delete img.dataset.srcset;
      }
      img.src = img.dataset.src;
      delete img.dataset.src;
    }

    /**
     * Maps a gallery media id to its slide index.
     * @param {string|number} mediaId - Shopify media id.
     * @returns {number} Slide index, or 0 if the media has no slide.
     */
    indexOfMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      return index === -1 ? 0 : index;
    }

    /** @returns {?Element} The image element of the active slide. */
    get activeImage() {
      const slide = this.slides[this.index];
      return slide ? slide.querySelector('.product-lightbox__img') : null;
    }

    /**
     * Returns the active image's untransformed bounding box, in client coords.
     * Measured with the transform cleared so the geometry stays trustworthy
     * regardless of the current zoom.
     * @returns {?DOMRect}
     */
    getBaseRect() {
      const img = this.activeImage;
      if (!img) return null;

      const previous = img.style.transform;
      img.style.transform = 'none';
      const rect = img.getBoundingClientRect();
      img.style.transform = previous;
      return rect;
    }

    /**
     * Sets how far this image may be zoomed. The ceiling is an absolute pixel
     * size — its own resolution times the upscale cap — so zoom and the CSS
     * size cap stop at exactly the same place.
     *
     * Where an image already fills that allowance there is nothing further to
     * gain, and the stage stops advertising zoom rather than offering an
     * interaction that does nothing.
     */
    updateMaxScale() {
      const img = this.activeImage;
      const base = this.getBaseRect();

      // NOT naturalWidth: for an image with srcset/sizes the spec applies a
      // density correction, so it reports layout-normalised size rather than
      // the file's real pixels. The width attribute is Liquid's own
      // preview_image.width, which is authoritative.
      const nativeWidth = parseInt(img && img.getAttribute('width'), 10)
        || (img && img.naturalWidth)
        || 0;

      if (!img || !base || !base.width || !nativeWidth) {
        // Not measurable yet (image still decoding). Withhold the zoom
        // affordance rather than advertise one that may not exist — this is
        // re-run on load, which is where the real answer comes from.
        this.maxScale = MAX_SCALE;
        this.stage.classList.add('is-zoom-unavailable');
        return;
      }

      const upscaleCap = parseFloat(this.dataset.upscaleCap) || 1;
      const nativeLimit = (nativeWidth * upscaleCap) / base.width;
      this.maxScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nativeLimit));
      this.stage.classList.toggle('is-zoom-unavailable', this.maxScale <= 1.01);
    }

    /** @returns {boolean} Whether this image has any real zoom headroom. */
    get canZoom() {
      return this.maxScale > 1.01;
    }

    /**
     * Writes the current scale and translation to the active image.
     */
    applyTransform() {
      const img = this.activeImage;
      if (!img) return;

      this.clampTranslation();
      img.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
      this.stage.classList.toggle('is-zoomed', this.scale > MIN_SCALE);
    }

    /**
     * Keeps the image covering the stage while zoomed, and centred at 1x.
     */
    clampTranslation() {
      const base = this.getBaseRect();
      if (!base || !base.width) return;

      const stage = this.stage.getBoundingClientRect();
      const scaledW = base.width * this.scale;
      const scaledH = base.height * this.scale;

      // Stage edges expressed relative to the image's own layout box.
      const left = stage.left - base.left;
      const right = stage.right - base.left;
      const top = stage.top - base.top;
      const bottom = stage.bottom - base.top;

      this.tx = scaledW >= right - left
        ? Math.min(left, Math.max(right - scaledW, this.tx))
        : (left + right) / 2 - scaledW / 2;

      this.ty = scaledH >= bottom - top
        ? Math.min(top, Math.max(bottom - scaledH, this.ty))
        : (top + bottom) / 2 - scaledH / 2;
    }

    /**
     * Zooms to a scale while holding one client point still under the cursor.
     * @param {number} nextScale - Target scale.
     * @param {number} clientX - Anchor point X, in client coords.
     * @param {number} clientY - Anchor point Y, in client coords.
     */
    zoomTo(nextScale, clientX, clientY) {
      const base = this.getBaseRect();
      if (!base) return;

      const target = Math.max(MIN_SCALE, Math.min(this.maxScale, nextScale));
      const x = clientX - base.left;
      const y = clientY - base.top;

      // Hold the local point under the anchor: t' = s - k' * (s - t) / k
      this.tx = x - ((x - this.tx) * target) / this.scale;
      this.ty = y - ((y - this.ty) * target) / this.scale;
      this.scale = target;

      this.applyTransform();
    }

    /**
     * Returns to 1x, centred.
     * @param {boolean} [animate=true] - Whether to animate.
     */
    resetZoom(animate = true) {
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;

      if (animate) {
        this.stage.classList.add('is-animating');
        clearTimeout(this.animationTimer);
        this.animationTimer = setTimeout(() => this.stage.classList.remove('is-animating'), 220);
      }

      this.applyTransform();
    }

    /**
     * Toggles between 1x and a middling zoom at a point.
     * @param {number} clientX - Anchor X.
     * @param {number} clientY - Anchor Y.
     */
    toggleZoom(clientX, clientY) {
      if (!this.canZoom && this.scale <= MIN_SCALE) return;

      this.stage.classList.add('is-animating');
      clearTimeout(this.animationTimer);
      this.animationTimer = setTimeout(() => this.stage.classList.remove('is-animating'), 220);

      if (this.scale > MIN_SCALE) {
        this.resetZoom(false);
      } else {
        this.zoomTo(Math.min(2.5, this.maxScale), clientX, clientY);
      }
    }

    /** Syncs the arrows and counter with the current slide. */
    updateControls() {
      if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.index >= this.slides.length - 1;
      if (this.counterCurrent) this.counterCurrent.textContent = String(this.index + 1);
    }

    /**
     * @param {PointerEvent} evt - Event object.
     */
    handlePointerDown(evt) {
      if (evt.button && evt.button !== 0) return;

      // Capture keeps a drag alive if the finger leaves the stage. It throws
      // for a pointer the element never received, which must not break zoom.
      try {
        this.stage.setPointerCapture(evt.pointerId);
      } catch (e) {
        // Non-fatal: panning still works without capture.
      }

      this.pointers.set(evt.pointerId, { x: evt.clientX, y: evt.clientY });
      this.stage.classList.remove('is-animating');

      if (this.pointers.size === 1) {
        this.gestureStart = { x: evt.clientX, y: evt.clientY, time: Date.now() };
        this.moved = false;
        if (this.scale > MIN_SCALE) this.stage.classList.add('is-panning');
      } else if (this.pointers.size === 2) {
        const [a, b] = Array.from(this.pointers.values());
        this.pinchDistance = Math.hypot(a.x - b.x, a.y - b.y);
        this.pinchMid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      }
    }

    /**
     * @param {PointerEvent} evt - Event object.
     */
    handlePointerMove(evt) {
      if (!this.pointers.has(evt.pointerId)) return;

      const previous = this.pointers.get(evt.pointerId);
      this.pointers.set(evt.pointerId, { x: evt.clientX, y: evt.clientY });

      if (this.pointers.size === 2) {
        const [a, b] = Array.from(this.pointers.values());
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };

        // Pan by however far the pinch centre travelled, then scale about it.
        this.tx += mid.x - this.pinchMid.x;
        this.ty += mid.y - this.pinchMid.y;

        if (this.pinchDistance > 0) {
          this.zoomTo((this.scale * distance) / this.pinchDistance, mid.x, mid.y);
        } else {
          this.applyTransform();
        }

        this.pinchDistance = distance;
        this.pinchMid = mid;
        this.moved = true;
        return;
      }

      if (this.gestureStart
        && (Math.abs(evt.clientX - this.gestureStart.x) > TAP_SLOP_PX
          || Math.abs(evt.clientY - this.gestureStart.y) > TAP_SLOP_PX)) {
        this.moved = true;
      }

      if (this.scale > MIN_SCALE) {
        this.tx += evt.clientX - previous.x;
        this.ty += evt.clientY - previous.y;
        this.applyTransform();
      }
    }

    /**
     * @param {PointerEvent} evt - Event object.
     */
    handlePointerUp(evt) {
      this.pointers.delete(evt.pointerId);
      if (this.stage.hasPointerCapture && this.stage.hasPointerCapture(evt.pointerId)) {
        this.stage.releasePointerCapture(evt.pointerId);
      }

      if (this.pointers.size < 2) {
        this.pinchDistance = 0;
      }

      if (this.pointers.size > 0) return;
      this.stage.classList.remove('is-panning');

      const wasTap = this.gestureStart
        && !this.moved
        && Date.now() - this.gestureStart.time < 500;

      if (wasTap) {
        const now = Date.now();
        if (now - this.lastTap < DOUBLE_TAP_MS) {
          this.toggleZoom(evt.clientX, evt.clientY);
          this.lastTap = 0;
        } else {
          this.lastTap = now;
          // A single tap on a mouse should zoom immediately rather than wait.
          if (evt.pointerType === 'mouse') this.toggleZoom(evt.clientX, evt.clientY);
        }
      } else if (this.scale <= MIN_SCALE && this.gestureStart && evt.pointerType !== 'mouse') {
        // Horizontal flick pages between images while unzoomed.
        const dx = evt.clientX - this.gestureStart.x;
        const dy = evt.clientY - this.gestureStart.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          this.goTo(this.index + (dx < 0 ? 1 : -1));
        }
      }

      this.gestureStart = null;
    }

    /**
     * @param {WheelEvent} evt - Event object.
     */
    handleWheel(evt) {
      if (!this.canZoom) return;
      evt.preventDefault();
      const factor = Math.exp(-evt.deltaY / 300);
      this.zoomTo(this.scale * factor, evt.clientX, evt.clientY);
    }

    /**
     * @param {KeyboardEvent} evt - Event object.
     */
    handleKeydown(evt) {
      switch (evt.key) {
        case 'Escape':
          evt.preventDefault();
          this.close();
          break;
        case 'ArrowLeft':
          this.goTo(this.index - 1);
          break;
        case 'ArrowRight':
          this.goTo(this.index + 1);
          break;
        default:
      }
    }

    handleResize() {
      this.updateMaxScale();
      this.resetZoom(false);
    }

    /**
     * @param {MouseEvent} evt - Event object.
     */
    handleClick(evt) {
      const close = evt.target.closest('[data-lightbox-close]');
      if (close) {
        evt.preventDefault();
        this.close();
        return;
      }

      const prev = evt.target.closest('.product-lightbox__prev');
      if (prev) {
        this.goTo(this.index - 1);
        return;
      }

      const next = evt.target.closest('.product-lightbox__next');
      if (next) this.goTo(this.index + 1);
    }
  }

  customElements.define('product-lightbox', ProductLightbox);
}
